/**
 * TipTap Custom Node: ViewSnapshot
 *
 * Block-Node der einen kompletten Viewer-Zustand speichert:
 * Kameraposition + Target + ClipBox-State.
 *
 * Wird als horizontale Karte im Dokument gerendert.
 * Beim Scrollen im Lese-Modus steuert der ScrollNavigator
 * den Viewer automatisch zu diesem Zustand.
 *
 * Syntax im Dokument:
 * { type: 'viewSnapshot', attrs: { snapshot: {...}, label: '...' } }
 */

import { Node, mergeAttributes } from '@tiptap/core'

export interface ViewSnapshotNodeOptions {
  /** Callback: wird aufgerufen wenn der Benutzer auf den Snapshot klickt */
  onSnapshotClick?: (snapshot: string) => void
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    viewSnapshot: {
      /** Fügt einen ViewSnapshot-Block an der aktuellen Cursorposition ein */
      insertViewSnapshot: (attrs: {
        snapshot: string   // JSON-stringified ViewSnapshot
        label: string
      }) => ReturnType
    }
  }
}

export const ViewSnapshotNode = Node.create<ViewSnapshotNodeOptions>({
  name: 'viewSnapshot',
  group: 'block',
  atom: true,
  // draggable wird NICHT gesetzt — eigenes Drag-System in EmbedDragPlugin.ts

  addOptions() {
    return {
      onSnapshotClick: undefined,
    }
  },

  addAttributes() {
    return {
      /** JSON-String des ViewSnapshot-Objekts */
      snapshot: {
        default: '{}',
        parseHTML: el => el.getAttribute('data-snapshot'),
        renderHTML: attrs => ({ 'data-snapshot': attrs.snapshot }),
      },
      /** Benutzer-Label für die Vorschau-Karte */
      label: {
        default: 'Ansicht',
        parseHTML: el => el.getAttribute('data-label'),
        renderHTML: attrs => ({ 'data-label': attrs.label }),
      },
      /** Pro-Snapshot: beim Verlassen zur vorherigen Ansicht zurückkehren */
      returnOnLeave: {
        default: false,
        parseHTML: el => el.getAttribute('data-return-on-leave') === 'true',
        renderHTML: attrs => ({ 'data-return-on-leave': String(!!attrs.returnOnLeave) }),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="view-snapshot"]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'view-snapshot',
        class: 'view-snapshot-block',
        contenteditable: 'false',
      }),
      HTMLAttributes['data-label'] ?? 'Ansicht',
    ]
  },

  addCommands() {
    return {
      insertViewSnapshot:
        attrs =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          })
        },
    }
  },
})
